import React from 'react';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Skeleton height (px value or tailwind class) */
  height?: string | number;
  /** Skeleton width (px value or tailwind class) */
  width?: string | number;
  /** Shape of the skeleton */
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  /** Whether animation is enabled */
  animation?: 'pulse' | 'wave' | false;
  /** Number of lines (for text variant) */
  lines?: number;
  /** Gap between lines (for text variant) */
  linesGap?: string | number;
}

export interface SkeletonTextProps extends Omit<SkeletonProps, 'variant'> {
  /** Number of lines */
  lines?: number;
  /** Gap between lines */
  linesGap?: string | number;
}

export interface SkeletonAvatarProps extends Omit<SkeletonProps, 'variant'> {
  /** Size of the avatar */
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface SkeletonButtonProps extends Omit<SkeletonProps, 'variant'> {
  /** Size of the button */
  size?: 'sm' | 'md' | 'lg';
  /** Whether the button is rounded */
  rounded?: boolean;
}

export interface SkeletonCardProps extends Omit<SkeletonProps, 'variant'> {
  /** Height of the card */
  height?: string | number;
  /** Whether to include header in the card */
  header?: boolean;
  /** Whether to include footer in the card */
  footer?: boolean;
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      height,
      width,
      variant = 'text',
      animation = 'pulse',
      lines = 1,
      linesGap = 6,
      className = '',
      style = {},
      ...props
    },
    ref
  ) => {
    // Base classes
    const baseClasses = `
      ${animation === 'pulse' ? 'animate-pulse' : ''}
      ${animation === 'wave' ? 'animate-skeleton-wave' : ''}
      bg-gray-200 dark:bg-gray-700
    `;
    
    // Variant classes
    const variantClasses = {
      text: 'rounded',
      circular: 'rounded-full',
      rectangular: '',
      rounded: 'rounded-lg',
    };
    
    // Custom styles
    const customStyle = {
      ...style,
      width: width ? (typeof width === 'number' ? `${width}px` : width) : undefined,
      height: height ? (typeof height === 'number' ? `${height}px` : height) : undefined,
    };
    
    // For text variant with multiple lines
    if (variant === 'text' && lines > 1) {
      const lineItems = [];
      const gap = typeof linesGap === 'number' ? `${linesGap}px` : linesGap;
      
      for (let i = 0; i < lines; i++) {
        // Make last line shorter for realistic text effect
        const lineWidth = i === lines - 1 && lines > 1 ? '70%' : '100%';
        
        lineItems.push(
          <div
            key={i}
            className={`${baseClasses} ${variantClasses[variant]}`}
            style={{
              width: lineWidth,
              height: height ? (typeof height === 'number' ? `${height}px` : height) : '1rem',
              marginBottom: i < lines - 1 ? gap : 0,
            }}
          />
        );
      }
      
      return (
        <div
          ref={ref}
          className={`flex flex-col ${className}`}
          style={{ width: width ? (typeof width === 'number' ? `${width}px` : width) : '100%' }}
          {...props}
        >
          {lineItems}
        </div>
      );
    }
    
    return (
      <div
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
        style={customStyle}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

export const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ lines = 3, linesGap = 6, height = 16, ...props }, ref) => {
    return (
      <Skeleton
        ref={ref}
        variant="text"
        lines={lines}
        linesGap={linesGap}
        height={height}
        {...props}
      />
    );
  }
);

SkeletonText.displayName = 'SkeletonText';

export const SkeletonAvatar = React.forwardRef<HTMLDivElement, SkeletonAvatarProps>(
  ({ size = 'md', ...props }, ref) => {
    const sizeMap = {
      sm: 32,
      md: 40,
      lg: 48,
      xl: 64,
    };
    
    const avatarSize = sizeMap[size];
    
    return (
      <Skeleton
        ref={ref}
        variant="circular"
        width={avatarSize}
        height={avatarSize}
        {...props}
      />
    );
  }
);

SkeletonAvatar.displayName = 'SkeletonAvatar';

export const SkeletonButton = React.forwardRef<HTMLDivElement, SkeletonButtonProps>(
  ({ size = 'md', rounded = false, ...props }, ref) => {
    const sizeMap = {
      sm: { height: 32, width: 80 },
      md: { height: 40, width: 100 },
      lg: { height: 48, width: 120 },
    };
    
    const buttonSize = sizeMap[size];
    
    return (
      <Skeleton
        ref={ref}
        variant={rounded ? 'rounded' : 'rectangular'}
        width={buttonSize.width}
        height={buttonSize.height}
        {...props}
      />
    );
  }
);

SkeletonButton.displayName = 'SkeletonButton';

export const SkeletonCard = React.forwardRef<HTMLDivElement, SkeletonCardProps>(
  ({ height = 200, header = false, footer = false, ...props }, ref) => {
    return (
      <div ref={ref} className="flex flex-col w-full overflow-hidden">
        {header && (
          <Skeleton
            variant="rectangular"
            height={48}
            width="100%"
            className="mb-4"
          />
        )}
        
        <Skeleton
          variant="rounded"
          height={height}
          width="100%"
          {...props}
        />
        
        {footer && (
          <Skeleton
            variant="rectangular"
            height={40}
            width="100%"
            className="mt-4"
          />
        )}
      </div>
    );
  }
);

SkeletonCard.displayName = 'SkeletonCard';

// Export as a namespace object for convenient usage
const SkeletonNamespace = Object.assign(Skeleton, {
  Text: SkeletonText,
  Avatar: SkeletonAvatar,
  Button: SkeletonButton,
  Card: SkeletonCard,
});

export default SkeletonNamespace;